export const STAR_LAUNCH_ELEMENT = "star-launch-surface";
export const STAR_LAUNCH_QUERY_KEYS = [
  "consoleLaunch",
  "consoleLaunchStartedAt",
  "consoleLaunchProgress",
] as const;

export type StarLaunchAppId =
  | "ai-3d"
  | "campus-memory"
  | "ai-eco"
  | "ai-video"
  | "digital-human"
  | "story-maker"
  | "course-center"
  | "inquiry-learning"
  | "world-view"
  | "insect-discovery"
  | "birdatlas"
  | "history-map"
  | "sail-ai"
  | "myth-map"
  | "university-map";

export type StarLaunchContext = {
  active: boolean;
  progress: number;
  startedAt: number | null;
};

export type StarLaunchDefinition = {
  id: StarLaunchAppId;
  name: string;
  title: string;
  copy: string;
  poster: string;
  video?: string;
  accent: string;
  accentSoft: string;
  mark: string;
};

export const starLaunchDefinitions: Record<StarLaunchAppId, StarLaunchDefinition> = {
  "ai-3d": {
    id: "ai-3d", name: "AI 3D", title: "3分钟，从想法到实物创作", copy: "模型正在被点亮",
    poster: "ai-3d-card.jpg", video: "ai-3d-transition.mp4", accent: "#38bdf8", accentSoft: "rgba(56,189,248,.42)", mark: "3D",
  },
  "campus-memory": {
    id: "campus-memory", name: "校园记忆", title: "把校园故事留在数字时光里", copy: "旧照片、声音与回忆正在被整理成可再次打开的校园档案。",
    poster: "campus-memory-card.jpg", video: "campus-memory-transition.mp4", accent: "#facc15", accentSoft: "rgba(250,204,21,.36)", mark: "记",
  },
  "ai-eco": {
    id: "ai-eco", name: "AI 生态", title: "一粒种子打开生态", copy: "种子破壳、根系展开，微光像网络一样连接到周围节点。",
    poster: "ai-eco-card.jpg", video: "ai-eco-transition.mp4", accent: "#a3e635", accentSoft: "rgba(163,230,53,.38)", mark: "芽",
  },
  "ai-video": {
    id: "ai-video", name: "AI影视", title: "镜头开始接管时间", copy: "分镜、素材与声画轨道被拉入同一个叙事窗口。",
    poster: "ai-video-card.jpg", video: "ai-video-transition.mp4", accent: "#fb7185", accentSoft: "rgba(251,113,133,.4)", mark: "▶",
  },
  "digital-human": {
    id: "digital-human", name: "AI数字人", title: "经典智慧，随问随答", copy: "AI 数字人正在打开经典辨析空间。",
    poster: "digital-human-card.jpg", video: "digital-human-transition.mp4", accent: "#7dd3fc", accentSoft: "rgba(125,211,252,.4)", mark: "人",
  },
  "story-maker": {
    id: "story-maker", name: "AI 教育故事", title: "把真实校园故事，做成四幕插画", copy: "故事、人物与场景正在被整理成连贯的四幕画面。",
    poster: "story-maker-card.jpg", accent: "#e8b65b", accentSoft: "rgba(232,182,91,.4)", mark: "画",
  },
  "course-center": {
    id: "course-center", name: "课程中心", title: "知识被整理成路径", copy: "书页、课程卡与进度轨迹汇聚成一条清晰的学习路线。",
    poster: "course-center-card.jpg", video: "course-center-transition.mp4", accent: "#22d3ee", accentSoft: "rgba(34,211,238,.38)", mark: "课",
  },
  "inquiry-learning": {
    id: "inquiry-learning", name: "格物探究", title: "让真实问题长成探究课题", copy: "问题、证据与行动路径正在汇入同一个探究工作台。",
    poster: "inquiry-learning-card.png", accent: "#6ee7b7", accentSoft: "rgba(110,231,183,.38)", mark: "究",
  },
  "world-view": {
    id: "world-view", name: "AI看世界", title: "开启自然观察之旅", copy: "昆虫、鸟类与更多探索项目正在被点亮。",
    poster: "world-view-card.jpg", video: "world-view-transition.mp4", accent: "#cbd5e1", accentSoft: "rgba(148,163,184,.36)", mark: "界",
  },
  "insect-discovery": { id:"insect-discovery", name:"昆虫探秘", title:"进入微观生命世界", copy:"移动光束、记录发现，再从图鉴回看观察证据。", poster:"insect-discovery-card.png", accent:"#84cc16", accentSoft:"rgba(132,204,22,.38)", mark:"虫" },
  birdatlas: { id:"birdatlas", name:"飞羽星图", title:"循着飞羽探索天空", copy:"追踪迁徙路线，并把鸟类观察整理成可解释的生态线索。", poster:"birdatlas-card.png", accent:"#38bdf8", accentSoft:"rgba(56,189,248,.38)", mark:"羽" },
  "history-map": { id:"history-map", name:"时空人物", title:"沿时间坐标遇见人物", copy:"人物、地点与时代关系正在同一张时空坐标中连接。", poster:"history-map-card.png", accent:"#f59e0b", accentSoft:"rgba(245,158,11,.38)", mark:"史" },
  "sail-ai": { id:"sail-ai", name:"航海探索", title:"驶入文明航海图", copy:"沿历史航线停靠港口，把判断依据写进自己的航海日志。", poster:"sail-ai-card.png", accent:"#22d3ee", accentSoft:"rgba(34,211,238,.38)", mark:"航" },
  "myth-map": { id:"myth-map", name:"世界神话", title:"打开文明神话谱系", copy:"从故事节点与相似母题之间，看见不同文明的想象方式。", poster:"myth-map-card.png", accent:"#fb7185", accentSoft:"rgba(251,113,133,.38)", mark:"神" },
  "university-map": { id:"university-map", name:"世界大学", title:"连接全球知识坐标", copy:"查看大学分布、求学流向与可核对的数据来源。", poster:"university-map-card.png", accent:"#60a5fa", accentSoft:"rgba(96,165,250,.38)", mark:"学" },
};

const clampProgress = (value: number, fallback = 8) =>
  Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : fallback;

export function readStarLaunchContext(search = globalThis.location?.search ?? ""): StarLaunchContext {
  const params = new URLSearchParams(search);
  const startedAt = Number(params.get("consoleLaunchStartedAt"));
  return {
    active: params.get("consoleLaunch") === "1",
    progress: clampProgress(Number(params.get("consoleLaunchProgress")), 8),
    startedAt: Number.isFinite(startedAt) && startedAt > 0 ? startedAt : null,
  };
}

export function clearStarLaunchParams(url: URL): URL {
  for (const key of STAR_LAUNCH_QUERY_KEYS) url.searchParams.delete(key);
  return url;
}

export function hasPendingStarLaunch(search = globalThis.location?.search ?? "", storedContext: string | null = null): boolean {
  const params = new URLSearchParams(search);
  if (params.has("handoff") && (params.get("consoleLaunch") === "1" || params.get("worldLaunch") === "1")) return true;
  if (!storedContext) return false;
  try { const value = JSON.parse(storedContext) as { expiresAt?: number }; return Boolean(value.expiresAt && value.expiresAt > Date.now()); }
  catch { return false; }
}

export type WorldLaunchPresentation = { active: boolean; progress: number; startedAt: number | null; title: string };

export function readWorldLaunchPresentation(search = globalThis.location?.search ?? "", storedContext: string | null = null): WorldLaunchPresentation {
  const params = new URLSearchParams(search);
  let stored: { progress?: number; startedAt?: number; title?: string; expiresAt?: number } | null = null;
  try { stored = storedContext ? JSON.parse(storedContext) : null; } catch { stored = null; }
  const direct = params.get("worldLaunch") === "1";
  const validStored = Boolean(stored?.expiresAt && stored.expiresAt > Date.now());
  const startedAt = Number(params.get("worldLaunchStartedAt") || stored?.startedAt);
  return {
    active: direct || validStored,
    progress: clampProgress(Number(params.get("worldLaunchProgress") || stored?.progress), 88),
    startedAt: Number.isFinite(startedAt) && startedAt > 0 ? startedAt : null,
    title: params.get("worldLaunchTitle") || stored?.title || "",
  };
}

const HTMLElementBase = (typeof HTMLElement === "undefined" ? class {} : HTMLElement) as typeof HTMLElement;

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
  })[character]!);
}

const styles = `
  :host { all:initial; position:fixed; inset:0; z-index:2147483200; display:block; overflow:hidden; color:#fff; color-scheme:dark; font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI","PingFang SC","Microsoft YaHei",sans-serif; opacity:1; transition:opacity .22s ease; }
  :host([hidden]) { display:none; }
  :host([leaving]) { opacity:0; pointer-events:none; }
  *,*::before,*::after { box-sizing:border-box; }
  .root { position:absolute; inset:0; overflow:hidden; background:#02040a; }
  .media { position:absolute; inset:0; overflow:hidden; background:#02040a; }
  .poster,.video { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
  .poster { opacity:.9; transform:scale(1.015); }
  .video { background:#02040a; }
  .wash { position:absolute; inset:0; background:radial-gradient(circle at 50% 44%,rgba(255,255,255,.06),transparent 42%),linear-gradient(90deg,rgba(2,6,23,.86) 0%,rgba(2,6,23,.18) 44%,rgba(2,6,23,.84) 100%),linear-gradient(180deg,rgba(2,6,23,.03) 0%,rgba(2,6,23,.88) 100%); }
  .halo { position:absolute; left:50%; top:42%; width:min(62vw,760px); aspect-ratio:1; translate:-50% -50%; border:1px solid color-mix(in srgb,var(--accent) 18%,transparent); border-radius:50%; box-shadow:0 0 120px var(--accent-soft),inset 0 0 90px rgba(255,255,255,.025); animation:breathe 3.4s ease-in-out infinite; }
  .orbit { position:absolute; left:50%; top:42%; width:min(46vw,560px); aspect-ratio:1; translate:-50% -50%; border:1px dashed color-mix(in srgb,var(--accent) 24%,transparent); border-radius:50%; animation:spin 18s linear infinite; }
  .content { position:relative; z-index:2; display:flex; height:100%; min-height:100%; align-items:flex-end; padding:0 5vw clamp(32px,7vh,72px); }
  .copy { width:min(100%,1040px); }
  .mark { display:grid; width:72px; height:72px; place-items:center; border:1px solid rgba(255,255,255,.2); border-radius:16px; background:rgba(255,255,255,.1); color:#fff; box-shadow:0 0 48px -12px var(--accent-soft); backdrop-filter:blur(18px); font-size:24px; font-weight:750; letter-spacing:-.04em; }
  .kicker { margin:20px 0 0; color:rgba(255,255,255,.6); font:600 12px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.32em; text-transform:uppercase; }
  h1 { max-width:1100px; margin:14px 0 0; color:#fff; font-size:clamp(36px,6vw,76px); font-weight:650; line-height:1.05; letter-spacing:-.035em; }
  .description { max-width:640px; margin:16px 0 0; color:rgba(255,255,255,.68); font-size:clamp(15px,1.5vw,19px); line-height:1.65; }
  .status { display:flex; align-items:center; justify-content:space-between; max-width:1040px; margin-top:26px; color:rgba(255,255,255,.62); font-size:12px; }
  .track { width:min(100%,1040px); height:4px; margin-top:9px; overflow:hidden; border-radius:999px; background:rgba(255,255,255,.13); }
  .bar { display:block; height:100%; width:0; border-radius:inherit; background:linear-gradient(90deg,color-mix(in srgb,var(--accent) 70%,#2563eb),var(--accent),#fff); box-shadow:0 0 28px var(--accent-soft); transition:width .3s ease-out; }
  .error { width:min(100%,640px); margin-top:22px; padding:16px; border:1px solid rgba(253,164,175,.3); border-radius:14px; background:rgba(0,0,0,.52); backdrop-filter:blur(18px); }
  .error p { margin:0; color:#ffe4e6; font-size:14px; line-height:1.55; }
  .actions { display:flex; flex-wrap:wrap; gap:10px; margin-top:13px; }
  button { min-height:40px; padding:0 16px; border:1px solid rgba(255,255,255,.22); border-radius:9px; color:#fff; background:rgba(255,255,255,.08); font:650 14px/1 inherit; cursor:pointer; }
  button.primary { border-color:#fff; color:#071018; background:#fff; }
  button:focus-visible { outline:3px solid color-mix(in srgb,var(--accent) 42%,transparent); outline-offset:2px; }
  @keyframes breathe { 50% { transform:scale(1.06); opacity:.72; } }
  @keyframes spin { to { transform:rotate(360deg); } }
  @media (max-width:640px) { .content { padding:0 22px 34px; } .mark { width:60px; height:60px; border-radius:14px; font-size:20px; } .description { max-width:92%; } }
  @media (prefers-reduced-motion:reduce) { :host { transition-duration:.01ms; } .halo,.orbit { animation:none; } .bar { transition-duration:.01ms; } }
`;

export class StarLaunchSurfaceElement extends HTMLElementBase {
  static get observedAttributes() {
    return ["app-id", "asset-base", "poster-src", "video-src", "phase", "progress", "error", "started-at", "play-video", "allow-login", "hidden"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    if (!this.hasAttribute("data-star-launch-preboot")) {
      document.querySelectorAll(`${STAR_LAUNCH_ELEMENT}[data-star-launch-preboot]`).forEach((element) => {
        if (element !== this) element.remove();
      });
    }
    this.syncDocumentLaunchState();
    this.render();
  }

  disconnectedCallback() {
    queueMicrotask(() => {
      if (document.querySelector(`${STAR_LAUNCH_ELEMENT}:not([hidden])`)) return;
      delete document.documentElement.dataset.starLaunchActive;
      window.dispatchEvent(new Event("star-launch-complete"));
    });
  }

  attributeChangedCallback(name: string) {
    if (!this.isConnected) return;
    if (name === "hidden") {
      this.syncDocumentLaunchState();
      return;
    }
    if (name === "phase" || name === "progress") this.updateStatus();
    else this.render();
  }

  private syncDocumentLaunchState() {
    if (!this.hidden) {
      document.documentElement.dataset.starLaunchActive = "true";
      window.dispatchEvent(new Event("star-launch-active"));
      return;
    }
    queueMicrotask(() => {
      if (document.querySelector(`${STAR_LAUNCH_ELEMENT}:not([hidden])`)) return;
      delete document.documentElement.dataset.starLaunchActive;
      window.dispatchEvent(new Event("star-launch-complete"));
    });
  }

  private definition(): StarLaunchDefinition {
    const appId = this.getAttribute("app-id") as StarLaunchAppId | null;
    return starLaunchDefinitions[appId ?? "world-view"] ?? starLaunchDefinitions["world-view"];
  }

  private assetUrl(explicit: string | null, filename: string): string {
    if (explicit) return explicit;
    return `${(this.getAttribute("asset-base") || "/platform-launch").replace(/\/$/, "")}/${filename}`;
  }

  private updateStatus() {
    const phase = this.getAttribute("phase") || "正在准备应用";
    const progress = clampProgress(Number(this.getAttribute("progress")), 8);
    const phaseNode = this.shadowRoot?.querySelector<HTMLElement>("[data-phase]");
    const output = this.shadowRoot?.querySelector<HTMLOutputElement>("output");
    const bar = this.shadowRoot?.querySelector<HTMLElement>(".bar");
    if (phaseNode) phaseNode.textContent = phase;
    if (output) output.textContent = `${Math.round(progress)}%`;
    if (bar) bar.style.width = `${progress}%`;
  }

  private seekVideo(video: HTMLVideoElement) {
    const startedAt = Number(this.getAttribute("started-at"));
    if (!Number.isFinite(startedAt) || startedAt <= 0 || !Number.isFinite(video.duration)) return;
    const elapsed = Math.max(0, (Date.now() - startedAt) / 1000);
    try { video.currentTime = video.duration > 0 ? elapsed % video.duration : 0; } catch { /* metadata may still be settling */ }
  }

  private render() {
    const definition = this.definition();
    const progress = clampProgress(Number(this.getAttribute("progress")), 8);
    const phase = this.getAttribute("phase") || "正在准备应用";
    const error = this.getAttribute("error")?.trim() || "";
    const poster = this.assetUrl(this.getAttribute("poster-src"), definition.poster);
    const video = this.getAttribute("video-src") || (definition.video ? this.assetUrl(null, definition.video) : "");
    const playVideo = this.hasAttribute("play-video") && Boolean(video);
    const loginAction = this.hasAttribute("allow-login")
      ? '<button data-action="login" type="button">使用账号登录</button>' : "";
    this.shadowRoot!.innerHTML = `
      <style>${styles}</style>
      <div class="root" style="--accent:${definition.accent};--accent-soft:${definition.accentSoft}" role="${error ? "alert" : "status"}" aria-label="${escapeHtml(definition.name)}正在启动">
        <div class="media">
          <img class="poster" alt="" src="${escapeHtml(poster)}" />
          ${playVideo ? `<video class="video" aria-hidden="true" autoplay loop muted playsinline preload="auto" poster="${escapeHtml(poster)}" src="${escapeHtml(video)}"></video>` : ""}
        </div>
        <div class="halo" aria-hidden="true"></div><div class="orbit" aria-hidden="true"></div><div class="wash" aria-hidden="true"></div>
        <div class="content"><div class="copy">
          <div class="mark" aria-hidden="true">${escapeHtml(definition.mark)}</div>
          <p class="kicker">${escapeHtml(definition.name)}</p>
          <h1>${escapeHtml(definition.title)}</h1>
          <p class="description">${escapeHtml(definition.copy)}</p>
          ${error ? `<div class="error"><p>${escapeHtml(error)}</p><div class="actions"><button class="primary" data-action="retry" type="button">重试进入</button>${loginAction}<button data-action="return" type="button">返回AI星链</button></div></div>` : `<div class="status"><span data-phase>${escapeHtml(phase)}</span><output>${Math.round(progress)}%</output></div><div class="track" aria-hidden="true"><span class="bar" style="width:${progress}%"></span></div>`}
        </div></div>
      </div>`;
    const media = this.shadowRoot!.querySelector<HTMLVideoElement>("video");
    if (media) {
      media.addEventListener("loadedmetadata", () => this.seekVideo(media), { once: true });
      void media.play().catch(() => { media.hidden = true; });
    }
    this.shadowRoot!.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((button) => {
      button.addEventListener("click", () => {
        const action = button.dataset.action;
        const accepted = this.dispatchEvent(new CustomEvent(`star-launch-${action}`, { bubbles: true, composed: true, cancelable: true }));
        if (!accepted || typeof window === "undefined") return;
        if (action === "retry") window.location.reload();
        if (action === "return") {
          const production = /(^|\.)zr-eduai\.com$/.test(window.location.hostname)
            && !window.location.hostname.includes("-test.")
            && window.location.hostname !== "console-test.zr-eduai.com";
          window.location.assign(this.getAttribute("return-href") || (production ? "https://star.zr-eduai.com/" : "https://console-test.zr-eduai.com/"));
        }
      });
    });
  }
}

if (typeof customElements !== "undefined" && !customElements.get(STAR_LAUNCH_ELEMENT)) {
  customElements.define(STAR_LAUNCH_ELEMENT, StarLaunchSurfaceElement);
}
